import { CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from 'components/tooltip/Tooltip';
import {
  getOpacity,
  getZIndexFromOpacity,
  isFromICS,
  isLeadASubBooking,
} from 'utils/lead/leadUtils';
import { Lead, LeadStatus } from 'models/Leads';
import { calendarStyleConstants } from 'pages/calendar/Calendar.constant';
import { normalizeKey } from 'utils/localeUtils';
import LeadTooltipBookingDetails from './LeadTooltipBookingDetails';

const LeadTooltip = ({
  lead,
  wrapperLeft,
  isOverlapping,
}: {
  lead: Lead;
  wrapperLeft?: number;
  isOverlapping?: boolean;
}) => {
  const { t } = useTranslation();
  const { creator, source, status } = lead;
  const { type: creatorType } = creator || {};

  const isGroupBooking = isLeadASubBooking(lead);

  const zIndex = 1039; // default modal z-index (1040) - 1, to appear below lead modal when clicked;

  const wrapperStyle = useMemo<CSSProperties>(
    () => ({
      position: 'absolute',
      width: wrapperLeft ? '100px' : '100%',
      height: '100%',
      left: `${wrapperLeft || 0}px`,
      top: '0px',
      cursor: 'pointer',
      zIndex: getZIndexFromOpacity(getOpacity(status)) + 200,
    }),
    [wrapperLeft, status],
  );

  const overlay = useMemo(
    () => (
      <>
        {isFromICS(source) ? (
          <span
            style={{ color: `${calendarStyleConstants.leadLabelColor.ICS}` }}
          >
            {t('pageCalendar.tooltip.importedFromIcal')}
            <br />
          </span>
        ) : null}
        {status === LeadStatus.BLOCKED ? (
          <>
            {t(
              normalizeKey(
                `pageCalendar.tooltip.blocked${
                  creatorType ? `_${creatorType}` : ''
                }`,
              ),
            )}
          </>
        ) : (
          <LeadTooltipBookingDetails
            lead={lead}
            // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            isOverlapping={isOverlapping}
            isGroupBooking={isGroupBooking}
          />
        )}
      </>
    ),
    [source, status, creatorType, isOverlapping, isGroupBooking, lead, t],
  );

  return (
    <Tooltip id="tooltip" text={overlay} placement="top" zIndex={zIndex}>
      <div style={wrapperStyle} />
    </Tooltip>
  );
};

LeadTooltip.defaultProps = {
  wrapperLeft: 0,
  isOverlapping: false,
};

export default LeadTooltip;
